import Box from '@mui/material/Box';
import { Styles } from '@t2c/shared-fe';
import React, { Fragment } from 'react';
import Stack from '@mui/material/Stack';
import { CircularProgress, Typography } from '@mui/material';
import { CardTypeEnum, ContentType, EMPTY_PAGE_TEXT } from '../../utils';
import EmptyPage from '../../Common/EmptyPage';
import { Button } from '@mui/material';
import { Grid } from '@mui/material';
import { AIMedia, AIMediaStatus, UploadTypes } from '../../@types';
import { FetchBrollCaptionParams } from 'apps/t2c-fe/services/broll-caption/fetchBrollCaptionData';
import { FiltersAndSorting } from '../../../t2c-fe/Common/FiltersAndSorting';
import { AudioCard } from '../text-to-speech/AudioCard';
import { useGeneratedMusicSection } from '../../../t2c-fe/hooks/ai-music/useGeneratedMusicSection';
import { RenameDialog } from '../text-to-speech/RenameDialog';
import { DeleteConfirmModal } from '../delete-confirm/DeleteConfirmModal';
import { InfiniteData } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';

interface Props {
  data: InfiniteData<AIMedia[]>;
  isLoading: boolean;
  refetch: () => void;
  allData?: boolean;
  params?: FetchBrollCaptionParams;
  setParams?: (_: FetchBrollCaptionParams) => void;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  handleItemClick?: (index: number | null, url?: string, name?: string) => void;
  selectedIndex?: number | null;
  isTimelinePage?: boolean;
}
export const GeneratedMusicsSection = ({
  data,
  isLoading,
  refetch,
  allData,
  params,
  setParams,
  fetchNextPage,
  hasNextPage,
  handleItemClick,
  selectedIndex,
  isTimelinePage = false,
}: Props) => {
  const {
    handleViewAll,
    filterOptions,
    handleFilterOptionChange,
    menuActions,
    handleShowMore,
    isOpenRenameModal,
    closeRenameModal,
    handleOnRename,
    isOpenDeleteModal,
    closeDeleteModal,
    confirmDeleteMusic,
    isDeletingMusic,
    selectedMusicData,
    isRenamingMusic,
  } = useGeneratedMusicSection({ params, setParams, refetchData: refetch });
  return (
    <Stack rowGap={3} pb={2} mt={4}>
      {!allData && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Recent Creations</Typography>
          <Button onClick={handleViewAll}>View All</Button>
        </Box>
      )}

      {allData && !isTimelinePage && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <FiltersAndSorting
            params={params}
            setParams={setParams}
            filterArray={filterOptions}
            handleFilterOptionChange={handleFilterOptionChange}
          />
        </Box>
      )}

      {isLoading ? (
        <Stack sx={styles.loader}>
          <CircularProgress />
        </Stack>
      ) : !(data?.pages?.[0] ?? []).length ? (
        <EmptyPage text={EMPTY_PAGE_TEXT.audio} type={ContentType.Audio} />
      ) : (
        <Box>
          {allData ? (
            <InfiniteScroll
              dataLength={data?.pages?.reduce((acc, page) => acc + page.length, 0) || 0}
              next={fetchNextPage}
              hasMore={hasNextPage}
              loader={
                <Box p={2} textAlign={'center'}>
                  <h2>loading...</h2>
                </Box>
              }
              scrollableTarget="scrollableDiv"
            >
              <Grid container spacing={4}>
                {data?.pages?.map((page, pageIndex) => (
                  <Fragment key={pageIndex}>
                    {page.map((res,index) => (
                      <Grid 
                        item 
                        flex={1} 
                        xs={12}                         
                        {...(!isTimelinePage && { sm: 6, md: 4, lg: 3 })}
                        key={res.id}
                        onClick={() => {
                          if(res.status === AIMediaStatus.Success && handleItemClick )
                            handleItemClick(index, res?.url, res.name);
                        }}
                      >
                        <AudioCard
                          isErrorStatus={false}
                          isInProgressStatus={false}
                          data={res}
                          audioUrl={res?.url}
                          menuActions={menuActions}
                          title={res?.name || ''}
                          isActive={selectedIndex === index}
                        />
                      </Grid>
                    ))}
                  </Fragment>
                ))}
              </Grid>
            </InfiniteScroll>
          ) : (
            <Grid container spacing={4}>
              {data?.pages?.map((page, pageIndex) => (
                <Fragment key={pageIndex}>
                  {page.map((res) => (
                    <Grid item flex={1} xs={12} sm={6} md={4} lg={3} key={res.id}>
                      <AudioCard
                        isErrorStatus={false}
                        isInProgressStatus={false}
                        data={res}
                        audioUrl={res?.url}
                        menuActions={menuActions}
                        title={res?.name || ''}
                      />
                    </Grid>
                  ))}
                </Fragment>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {isOpenRenameModal && (
        <RenameDialog
          dialogTitle="Rename Music"
          handleCloseDialog={closeRenameModal}
          shouldOpenDialog={isOpenRenameModal}
          handleOnRename={handleOnRename}
          initialName={selectedMusicData?.name}
          isLoading={isRenamingMusic}
        />
      )}
      {isOpenDeleteModal && (
        <DeleteConfirmModal
          handleCancel={closeDeleteModal}
          open={isOpenDeleteModal}
          id={selectedMusicData?.id}
          name={selectedMusicData?.name}
          refetchData={refetch}
          type={CardTypeEnum.AIMedia}
        />
      )}
    </Stack>
  );
};

const styles: Styles = {
  cardContainer: {
    minHeight: 180,
  },
  card: {
    width: 360,
  },
  loader: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20%',
  },
};
